// recovery-op-journal.ts
//
// Orchestrator recovery (3.40.0): the journal writes of a recovery op. Order
// (B18): intent BEFORE the ticket file + git commit, result AFTER — a crash
// in between leaves an intent without result, which the next call resumes
// with the same `params.ticket_id` (recovery-ticket-materializer.ts).
import { randomUUID } from "node:crypto";
import type { MissionStore } from "./mission-store.js";
import {
  RECOVERY_SKIP_OP_KIND,
  RECOVERY_TICKET_OP_KIND,
  type RecoverySkipReason,
} from "./recovery-guards.js";
import { recoverySkipOpId } from "./recovery-ticket-shape.js";

export interface RecoveryTriggerRef {
  source_ticket: string;
  trigger: string;
  trigger_seq: number;
}

export async function commitRecoveryTicketIntent(
  store: MissionStore,
  operationId: string,
  ref: RecoveryTriggerRef,
  ticketId: string,
): Promise<void> {
  await store.commit([
    {
      event_id: `${operationId}-intent`,
      operation_id: operationId,
      delta: {
        kind: "operation_intent",
        operation_id: operationId,
        operation_kind: RECOVERY_TICKET_OP_KIND,
        params: { ...ref, ticket_id: ticketId },
      },
    },
  ]);
}

export async function commitRecoveryTicketFailed(
  store: MissionStore,
  operationId: string,
): Promise<void> {
  await store.commit([
    {
      event_id: `${operationId}-result`,
      operation_id: operationId,
      delta: { kind: "operation_result", operation_id: operationId, outcome: "failed" },
    },
  ]);
}

/** Result `applied` (+ the committed content's hash, H-1) and the recovery ticket's `todo` in ONE commit. */
export async function commitRecoveryTicketApplied(
  store: MissionStore,
  operationId: string,
  ticketId: string,
  ticketSha256: string,
): Promise<void> {
  await store.commit([
    {
      event_id: `${operationId}-result`,
      operation_id: operationId,
      delta: {
        kind: "operation_result",
        operation_id: operationId,
        outcome: "applied",
        ticket_sha256: ticketSha256,
      },
    },
    {
      event_id: randomUUID(),
      delta: { kind: "ticket_state_changed", ticket: ticketId, status: "todo" },
    },
  ]);
}

export async function commitRecoverySkip(
  store: MissionStore,
  ref: RecoveryTriggerRef,
  reason: RecoverySkipReason,
): Promise<void> {
  const operationId = recoverySkipOpId(ref.source_ticket, ref.trigger, ref.trigger_seq);
  await store.commit([
    {
      event_id: `${operationId}-intent`,
      operation_id: operationId,
      delta: {
        kind: "operation_intent",
        operation_id: operationId,
        operation_kind: RECOVERY_SKIP_OP_KIND,
        params: { ...ref, reason },
      },
    },
    {
      event_id: `${operationId}-result`,
      operation_id: operationId,
      delta: { kind: "operation_result", operation_id: operationId, outcome: "skipped" },
    },
  ]);
}
